// Engineering changes (ECO) — data access (docs/design/engineering-changes.md, ADR-045).
//
//   list     rpc engineering_change_page(company, status, term, filters, sort, after, limit) —
//            newest first, keyset (created_at, number) on engineering_changes_list_idx.
//   detail   rpc engineering_change_document(id) — header, items with their EBOM
//            revision and MBOM version, PBOMs to review, open orders on an older
//            version; one round-trip.
//   writes   save_engineering_change, engineering_change_action(approve | reopen |
//            implement | close | cancel) — expected revision, PT409 when stale.

import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import type { TFunction } from "i18next";
import { sortParam, type EcoStatus, type ListFilter, type ListSort } from "@patronix/domain/list";
import type { Page } from "@/components/DataList";
import { isDemoMode, supabase } from "@/lib/supabase";

export type EcoFilter = "open" | EcoStatus | "all";
export const ecoFilters: EcoFilter[] = ["open", "draft", "approved", "implemented", "closed", "cancelled", "all"];

export interface EcoListParams {
  status: EcoFilter;
  q: string;
  filters?: ListFilter[];
  sort?: ListSort | null;
}

export interface EcoRow {
  id: string;
  number: string;
  title: string;
  status: EcoStatus;
  effective_date: string;
  item_count: number;
  created_at: string;
  approved_at: string | null;
  implemented_at: string | null;
}

export interface EcoItem {
  line_no: number;
  item_id: string;
  item_code: string;
  item_description: string;
  procurement_type: "purchase" | "manufacture";
  note: string | null;
  ebom: { id: string; rev: string; status: "draft" | "released" | "obsolete"; line_count: number } | null;
  mbom: { id: string; version: number; status: "draft" | "approved" | "obsolete"; valid_from: string } | null;
}

export interface EcoDocument {
  id: string;
  company_id: string;
  number: string;
  title: string;
  reason: string | null;
  effective_date: string;
  status: EcoStatus;
  item_count: number;
  revision: number;
  created_at: string;
  created_by_name: string | null;
  approved_at: string | null;
  approved_by_name: string | null;
  implemented_at: string | null;
  implemented_by_name: string | null;
  closed_at: string | null;
  items: EcoItem[];
  pboms: { id: string; item_code: string; version: number; problem: string | null }[];
  orders: { id: string; number: string; status: string; item_code: string; start_date: string; bom_version: number | null }[];
}

export type EcoAction = "approve" | "reopen" | "implement" | "close" | "cancel";

export const ECO_PAGE = 50;
const STALE = 30_000;

function db() {
  if (!supabase) throw new Error("Supabase is not configured");
  return supabase;
}

function noDemo() {
  if (isDemoMode) throw new Error("Engineering changes are not available in demo mode");
}

export async function listEcos(companyId: string, p: EcoListParams, cursor: string | null, signal: AbortSignal, size = ECO_PAGE): Promise<Page<EcoRow>> {
  if (isDemoMode || !companyId) return { rows: [], nextCursor: null };
  const { data, error } = await db()
    .rpc("engineering_change_page", {
      p_company_id: companyId,
      p_status: p.status,
      p_term: p.q.trim() || null,
      p_filters: p.filters ?? [],
      p_sort: sortParam(p.sort ?? null),
      p_after: cursor ? JSON.parse(cursor) : null,
      p_limit: size + 1,
    })
    .abortSignal(signal);
  if (error) throw error;
  const rows = data as (EcoRow & { sort_cursor: unknown })[];
  const hasMore = rows.length > size;
  const page = hasMore ? rows.slice(0, size) : rows;
  const last = page[page.length - 1];
  return { rows: page, nextCursor: hasMore && last ? JSON.stringify(last.sort_cursor) : null };
}

async function getEco(id: string, signal: AbortSignal): Promise<EcoDocument | null> {
  if (isDemoMode) return null;
  const { data, error } = await db().rpc("engineering_change_document", { p_id: id }).abortSignal(signal);
  if (error) throw error;
  return (data as EcoDocument | null) ?? null;
}

export async function saveEco(
  id: string | null,
  companyId: string,
  header: { title: string; reason: string | null; effective_date: string; expected_revision?: number },
  items: { item_id: string; note: string | null }[],
): Promise<{ id: string; number: string; revision: number }> {
  noDemo();
  const { data, error } = await db().rpc("save_engineering_change", { p_id: id, p_company_id: companyId, p_header: header, p_items: items });
  if (error) throw error;
  return data as { id: string; number: string; revision: number };
}

export async function ecoAction(id: string, action: EcoAction, revision: number): Promise<void> {
  noDemo();
  const { error } = await db().rpc("engineering_change_action", { p_id: id, p_action: action, p_expected_revision: revision });
  if (error) throw error;
}

export const ecoKeys = {
  all: ["engineering", "changes"] as const,
  list: (companyId: string, p: EcoListParams) => [...ecoKeys.all, "list", companyId, p] as const,
  detail: (id: string) => [...ecoKeys.all, "detail", id] as const,
};

export function ecoListQuery(companyId: string, p: EcoListParams) {
  return infiniteQueryOptions({
    queryKey: ecoKeys.list(companyId, p),
    queryFn: ({ pageParam, signal }) => listEcos(companyId, p, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: Page<EcoRow>) => last.nextCursor,
    staleTime: STALE,
  });
}

export function ecoDetailQuery(id: string) {
  return queryOptions({ queryKey: ecoKeys.detail(id), queryFn: ({ signal }) => getEco(id, signal), staleTime: STALE });
}

const rules: [RegExp, string][] = [
  [/changed by someone else/, "eco.err.conflict"],
  [/^Only a draft engineering change can be changed/, "eco.err.notDraft"],
  [/^Title: 1 to 160/, "eco.err.title"],
  [/^Date: within a year back/, "eco.err.date"],
  [/^An engineering change holds 1 to 200 items/, "eco.err.items"],
  [/^Missing permission/, "eco.err.permission"],
];

export function ecoErrorMessage(t: TFunction, error: unknown): string {
  const tr = t as unknown as (key: string, o?: Record<string, string>) => string;
  const message = (error as { message?: string } | null)?.message ?? String(error ?? "");
  const rule = rules.find(([re]) => re.test(message));
  return rule ? tr(rule[1]) : message || tr("auth.errorGeneric");
}
